var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import Linkify from 'react-linkify';
import { getCssThemeColorVariable } from '@theme';
import { Text as ChakraText, } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useSafeHtml } from '@view/hooks/useSafeHtml';
const HTMLContainer = styled(ChakraText) `
  /*
   * Normalize the font of property-authored HTML to the Booking Engine body
   * font. The css-reset sets the body font on the scope container by plain
   * inheritance (no !important), so an inline font-family carried in pasted
   * content (e.g. from Word/Google Docs) would otherwise win. Forcing inherit
   * with !important beats that non-important inline style (BENG-4192).
   */
  * {
    text-align: initial;
    font-family: inherit !important;
  }

  /*
   * Keep the css-reset's monospace font for code/preformatted content. The
   * universal font-family override above (needed to beat inline styles) would
   * otherwise force these to the body font too. Mirrors css-reset.tsx.
   */
  pre, code, kbd, samp {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace !important;
  }

  img {
    object-fit: contain;
  }

  ol, ul {
    padding-left: 20px;
  }

  /*
   * If a property author inserts a blank paragraph to add spacing, render it as
   * a consistent one-line gap. A <p>&nbsp;</p> already produces one line, but a
   * truly-empty <p></p> generates no line box and collapses to zero height (the
   * global reset zeroes <p> margins), so that gap would silently disappear. A
   * one-line min-height on every paragraph makes empty spacer paragraphs occupy
   * a line too, so any author-added spacing renders consistently. 1.5em matches
   * the theme base line-height (foundations/lineHeights.ts).
   */
  p {
    min-height: 1.5em;
  }

  *:is(h1, h2, h3, h4, h5, h6) {
    font-weight: 600;
  }
`;
export const Text = forwardRef(function Text(_a, ref) {
    var { children, className, disableLink = false, html, lineClamp, testid, color: _color } = _a, textProps = __rest(_a, ["children", "className", "disableLink", "html", "lineClamp", "testid", "color"]);
    const color = `var(${getCssThemeColorVariable(_color)})`;
    // Title any untitled <iframe> in injected HTML here, at the source, so every
    // dangerouslySetInnerHTML consumer is covered (WCAG 4.1.2, BENG-4060 #52).
    const safeHtml = useSafeHtml(html);
    const commonProps = {
        className: ['cb-text', className].filter(Boolean).join(' '),
        color,
        maxHeight: lineClamp === null || lineClamp === void 0 ? void 0 : lineClamp.maxHeight,
        maxWidth: '100%',
        noOfLines: lineClamp === null || lineClamp === void 0 ? void 0 : lineClamp.noOfLines,
        sx: { '--cb-text--color': color },
    };
    return safeHtml ? (
    // Render as a div: arbitrary HTML may contain block-level elements
    // (e.g. <div>), which are invalid inside the default <p> element.
    _jsx(HTMLContainer, Object.assign({ as: "div", dangerouslySetInnerHTML: { __html: safeHtml }, "data-be-text": true, "data-testid": testid, ref: ref }, commonProps, textProps))) : (_jsx(ChakraText, Object.assign({ "data-be-text": true, "data-testid": testid, ref: ref }, commonProps, textProps, { children: disableLink ? children : _jsx(Linkify, { children: children }) })));
});
